import { useAuth } from './useAuth';

/**
 * Read-only access to the current user's effective permissions in the active
 * org. These checks are for UX only (hiding/disabling controls); the backend
 * remains the enforcement point.
 */
const usePermissions = () => {
    const { permissions, isStaff, isSuperAdmin, isLoading, activeOrg, organization } = useAuth();

    const can = (permission: string): boolean => permissions.includes(permission);
    const canAny = (perms: string[]): boolean =>
        perms.some(permission => permissions.includes(permission));
    const canAll = (perms: string[]): boolean =>
        perms.every(permission => permissions.includes(permission));

    /**
     * Whether we've switched into an org that isn't our own — the same test
     * OrgSwitcher uses to label itself.
     */
    const isSwitched = !!activeOrg && !!organization && activeOrg.id !== organization.id;

    /**
     * Whether we're acting inside a customer org: switched, and switched into
     * something that isn't Watchly's own org.
     *
     * Deliberately keyed off the *switch* rather than off `activeOrg.isInternal`
     * alone. Platform roles are granted independently of org membership
     * (User::hasPlatformRole ignores team context), and legacy super admins
     * sitting in a customer org do exist — AdminUserController::removePlatformRole
     * exists precisely to clean those up. Gating on `isInternal` alone would lock
     * those users out of /admin/users, which is the page needed to fix the
     * situation. Nobody is ever hidden from their own home org's tooling.
     */
    const inCustomerContext = isSwitched && !(activeOrg?.isInternal ?? false);

    /**
     * Internal tooling is hidden while viewing a customer: it's confusing next
     * to that customer's data, and it's the kind of thing you don't want on
     * screen when screen-sharing with them. These are UX gates only — the
     * backend still authorizes by platform role, so exiting the customer
     * context restores everything.
     */
    const showSalesTools = isStaff && !inCustomerContext;
    const showAdminTools = isSuperAdmin && !inCustomerContext;

    return {
        can,
        canAny,
        canAll,
        permissions,
        isStaff,
        isSuperAdmin,
        inCustomerContext,
        showSalesTools,
        showAdminTools,
        isLoading,
    };
};

export { usePermissions };
